"use client";

import { FeedEvents, trackEvent } from "@/analytics";

import type { ComedianWithImageUrl } from "@/components/feed/types";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { Follow } from "@/components/checkout";
import Image from "next/image";
import Link from "next/link";
import { PunchupButton } from "@punchup/ui-common/punchup/primitives/buttons/PunchupButton";
import { PunchupConversionCard } from "@punchup/ui-common/punchup/composites/marketing/PunchupConversionCard";
import RailModule from "./RailModule";
import RailShowsNearYou from "./RailShowsNearYou";

const MAX_COMEDIANS = 4;

/**
 * The logged-out desktop right rail (xl+): the sticky sign-up card, comedians
 * to subscribe to, and shows near the visitor — the Substack-style persistent
 * funnel next to the feed. Rendered only under the feed_brand_moment gate; the
 * in-feed suggested-comedians / nearby-shows units hide at xl+ while this is
 * on (FeedPostsV2) so discovery lives in exactly one place per breakpoint.
 */
export default function FeedRail({
  suggestedComedians,
}: {
  suggestedComedians: ComedianWithImageUrl[];
}) {
  const comedians = suggestedComedians.slice(0, MAX_COMEDIANS);
  const glowFaces = suggestedComedians
    .filter((comedian) => comedian.imageUrl)
    .slice(0, 6)
    .map((comedian) => comedian.imageUrl as string);

  return (
    <div className="flex flex-col gap-4">
      <PunchupConversionCard
        tone="ink"
        decorative={
          glowFaces.length >= 3 ? (
            // Ambient glow: artist photos hugely blurred — a still color wash,
            // no literal headshots.
            <div className="absolute -inset-10 grid grid-cols-3 grid-rows-2 blur-xl saturate-150 opacity-50">
              {glowFaces.map((url, i) => (
                <div key={`glow-${i}`} className="relative">
                  <Image
                    src={url}
                    alt=""
                    fill
                    sizes="120px"
                    className="object-cover"
                  />
                </div>
              ))}
            </div>
          ) : undefined
        }
        heading="Join the community"
        line="Follow your favorites, watch exclusive specials, and never miss a show."
        actions={
          <>
            <PunchupButton asChild>
              <Link
                href="/signin"
                onClick={() =>
                  trackEvent(FeedEvents.CLICKED_FEED_CONVERSION_CARD, {
                    placement: "rail",
                    target: "sign-up",
                  })
                }
              >
                Sign up
              </Link>
            </PunchupButton>
            <PunchupButton variant="secondary-inverse" asChild>
              <Link
                href="/signin"
                onClick={() =>
                  trackEvent(FeedEvents.CLICKED_FEED_CONVERSION_CARD, {
                    placement: "rail",
                    target: "sign-in",
                  })
                }
              >
                Sign in
              </Link>
            </PunchupButton>
          </>
        }
      />

      {comedians.length > 0 && (
        <RailModule
          title="Artists you might like"
          seeAllHref="/discover"
          onSeeAllClick={() =>
            trackEvent(FeedEvents.CLICKED_FEED_RAIL_ITEM, {
              module: "artists-you-might-like",
              target: "see-all",
            })
          }
        >
          {comedians.map((comedian) => (
            <div
              key={comedian.id}
              className="flex items-center justify-between gap-2 min-w-0"
            >
              <Link
                href={`/${comedian.slug}`}
                onClick={() =>
                  trackEvent(FeedEvents.CLICKED_FEED_RAIL_ITEM, {
                    module: "artists-you-might-like",
                    target: "comedian",
                    comedianId: comedian.id,
                  })
                }
                className="flex items-center gap-2.5 min-w-0 group"
              >
                <div className="relative w-10 h-10 rounded-full overflow-hidden bg-neutral-100 shrink-0">
                  {comedian.imageUrl && (
                    <Image
                      src={comedian.imageUrl}
                      alt={comedian.display_name ?? "Comedian"}
                      fill
                      className="object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--punchup-color-ink-1000)] truncate group-hover:underline">
                    {comedian.display_name}
                  </p>
                  <p className="text-xs text-[var(--punchup-color-ink-500)]">
                    Comedian
                  </p>
                </div>
              </Link>
              <Follow
                isLoggedOutState
                redirectTo={`/${comedian.slug}`}
                buttonText="Subscribe"
                followedLocation={FOLLOW_LOCATIONS.FEED_SUGGESTED_COMEDIAN_CARD}
                ctaSource="feed_rail"
                comedianToFollow={comedian}
                customCSSButtonClass="text-primary bg-[var(--punchup-color-primary-soft)] hover:bg-[#fad9cc] font-medium text-xs py-1.5 px-3 rounded-md transition-colors ease-in-out duration-200 shrink-0"
                customCSSContainerClass="block shrink-0"
              />
            </div>
          ))}
        </RailModule>
      )}

      <RailShowsNearYou />
    </div>
  );
}
